import React from "react";
import ModalForm from "@ui/Modal/Form";
import t from "../../../../../../i18n";
import { css } from "@linaria/core";
import useInput from "@ui/lib/hooks/useInput";
import InputV2 from "@ui/Input/InputV2";
import { useFaultEvents, type FaultEvent } from "../../../../../../api/useFaultEvents";
import { generateFaultEventPayload } from "diagnostics-domain/faultEvent";
import Select from "@ui/Select";
import useToasts from "@ui/Toast/useToasts";
import { color } from "@ui";
import { spacings } from "@ui/Theme";
import { comparatorBy } from "diagnostics-lib/comparator";
import { indexBy } from "diagnostics-lib/iterable";
import ModalFormMergeEventSummary from "./ModalFormMergeEventSummary";
import type { Project } from "../../../../../../api/useProjects";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import formatDate from "@ui/i18n/formatDate";
import { getEventStatusText } from "../../../utils/faultEventStatus";
import { usePost } from "../../../../../../api/useCall";
import { useRevalidate } from "../../../../../../api/useQuery";
import { useNavigate } from "react-router";
import { isLeft } from "fp-ts/lib/Either";
import { short } from "diagnostics-lib/io";
import * as io from "io-ts";
import { toPath } from "../../../../../../router/helpers/path";
interface ModalFormMergeEventProps {
  project: Project;
  event: FaultEvent;
  siblingEvents: FaultEvent[];
  onRequestClose: () => void;
}

const PATH = "/projects/:projectId(uuid)/faultEvents/merge" as const;
const REVALIDATE_PATH = "/projects/:projectId(uuid)/faultEvents" as const;

const ModalFormMergeEvent = ({
  project,
  event,
  siblingEvents,
  onRequestClose,
}: ModalFormMergeEventProps) => {
  const { addToast } = useToasts();
  const navigate = useNavigate();

  const mergeEventCall = usePost(PATH);
  const revalidate = useRevalidate();

  const events = useFaultEvents(project.id).events;
  const indexedEvents = React.useMemo(() => indexBy(events, "id"), [events]);

  const [mergeEvent, setMergeEvent] = React.useState<string>(
    siblingEvents.length === 1 ? siblingEvents[0]!.id : "",
  );

  const siblingOptions = React.useMemo(
    () =>
      siblingEvents
        .map(siblingEvent => {
          const description = `${formatDate(siblingEvent.startsAt, "slash", project.timezone)} - ${formatDate(siblingEvent.endsAt, "slash", project.timezone)}, ${getEventStatusText(siblingEvent.metadata.status)}`;
          return {
            value: siblingEvent.id,
            label: siblingEvent.name,
            description,
          };
        })
        .sort(comparatorBy(s => s.label)),
    [siblingEvents, project.timezone],
  );

  const {
    defaultValue: defaultNewEventName,
    value: newEventName,
    onChange: onNewEventNamChange,
    dirty: newEventNameDirty,
  } = useInput(event.name);

  const eventToMerge = React.useMemo(
    () => indexedEvents.get(mergeEvent),
    [indexedEvents, mergeEvent],
  );

  const errorNewTargetEventName = React.useMemo(() => {
    if (newEventNameDirty && newEventName.trim().length === 0)
      return t("project.fault_event.modal.form.merge_event.input.error.empty_field");

    if (isLeft(short(io.string).decode(newEventName.trim())))
      return t(
        "project.fault_event.modal.form.input.fault.name.error.cannot_exceed_100_characters",
      );

    return;
  }, [newEventName, newEventNameDirty]);

  const dirty = errorNewTargetEventName === undefined && mergeEvent !== "";

  const handleSubmit = React.useCallback(async () => {
    if (mergeEvent === "") return false;

    const sourceEvent = indexedEvents.get(mergeEvent);
    if (!sourceEvent) return false;

    try {
      const results = await mergeEventCall({
        routeParams: { projectId: project.id },
        body: {
          sourceEvent: generateFaultEventPayload({ faultEvent: sourceEvent }),
          targetEvent: generateFaultEventPayload({
            faultEvent: event,
          }),
          newNameEvent: newEventName,
        },
      });
      await revalidate(REVALIDATE_PATH);
      void navigate(
        toPath("/projects/:projectId/fault-events/:faultEventId/fault", {
          projectId: project.id,
          faultEventId: results.body.mergeEvent.id,
        }),
        { replace: true },
      );
      addToast("success", t("project.fault_event.modal.form.merge_event.toast.success"));
      onRequestClose();
      return true;
    } catch (error) {
      addToast("error", t("errors.backend_error"));
      return false;
    }
  }, [
    navigate,
    addToast,
    revalidate,
    mergeEventCall,
    onRequestClose,
    indexedEvents,
    event,
    mergeEvent,
    newEventName,
    project.id,
  ]);

  return (
    <ModalForm
      className={modalForm}
      onSubmit={dirty ? handleSubmit : false}
      onRequestClose={onRequestClose}
      okButtonName={t("project.fault_event.modal.form.merge_event.button.name.ok")}
    >
      <InputV2
        className={input}
        type="text"
        label={t("project.fault_event.modal.form.merge_event.input.new_event.label")}
        placeholder={defaultNewEventName}
        defaultValue={defaultNewEventName}
        value={newEventName}
        onChange={onNewEventNamChange}
        revertable={newEventNameDirty}
        error={errorNewTargetEventName}
      />

      <Select
        className={select}
        label={t("project.fault_event.modal.form.merge_event.select.label")}
        prompt={t("project.fault_event.common.select.prompt")}
        value={mergeEvent}
        options={siblingOptions}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setMergeEvent(e.target.value)}
        tooltipOverlay={t("project.fault_event.modal.form.merge_event.select.tooltip")}
        clearable
      />

      {dirty && eventToMerge !== undefined && (
        <>
          <div className={divider} />
          <ModalFormMergeEventSummary
            project={project}
            sourceEvent={eventToMerge}
            targetEvent={event}
            targetEventName={newEventName}
          />
          <AdditionnalMessage
            className={messageInfo}
            status="INFO"
            text={t("project.fault_event.modal.form.merge_event.message.redirection")}
          />
        </>
      )}
    </ModalForm>
  );
};

export default ModalFormMergeEvent;

//#region CSS
const modalForm = css`
  margin: 12px 0;
  row-gap: 0;
`;
const input = css`
  width: fit-content;
  min-width: 368px;
`;
const select = css`
  margin-top: ${spacings.l};
  width: fit-content;
  min-width: 368px;
`;
const divider = css`
  margin: ${spacings.l} 0;
  height: 1px;
  width: 100%;
  background-color: ${color.titanium200};
`;
const messageInfo = css`
  margin-top: ${spacings.l};
`;
//#endregion
